import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Collapse,
  Button
} from '@mui/material';
import {
  PlayArrow,
  Code,
  Support,
  Settings,
  Description,
  Phone,
  Chat,
  PhoneAndroid,
  Web,
  PhoneIphone,
  Security,
  Schedule,
  FormatListNumbered,
  PlayCircleOutline,
  ExpandLess,
  ExpandMore,
  Videocam,
  VideoCall,
  Call,
  Notifications,
  Api,
  IntegrationInstructions,
  RocketLaunch,
  Build,
  Groups,
  People,
  VpnKey,
  Lock,
  Info,
  HelpOutline,
  Article,
  MenuBook,
  Terminal,
  Extension,
  Widgets,
  Cloud,
  Download,
  GetApp,
  BugReport,
  Lightbulb,
  Link,
  Language,
  Devices,
  Smartphone,
  Computer,
  Storage,
  Sync,
  School,
  LibraryBooks,
  Home,
  Star,
  CheckCircle,
  Warning,
  Forum,
  Message,
  Mic,
  ScreenShare,
  Event,
  CalendarMonth,
  AccessTime,
  Tune,
  Analytics,
  Hub,
  AccountTree,
  DataObject,
  TipsAndUpdates,
  Campaign,
  Key,
  LockOpen,
  Visibility,
  Search,
  FilterList,
  Dashboard,
  Layers,
  Category,
  Construction,
  Handyman,
  Speed,
  Memory,
  Science,
  Psychology
} from '@mui/icons-material';
import { DocumentationSection, SDKOption } from '../types/documentation';

interface DocumentationDrawerProps {
  open: boolean;
  onClose: () => void;
  sections: DocumentationSection[];
  selectedSectionId: string;
  onSectionSelect: (sectionId: string) => void;
  sdkOptions: SDKOption[];
  selectedSdk: string;
  onSdkChange: (sdkId: string) => void;
  showSdkSelector?: boolean;
  onGettingStartedClick?: () => void;
  kitType?: 'sdk' | 'uikit';
}

const iconMap: { [key: string]: React.ReactElement } = {
  PlayArrow: <PlayArrow />,
  Code: <Code />,
  Support: <Support />,
  Settings: <Settings />,
  Description: <Description />,
  Phone: <Phone />,
  Chat: <Chat />,
  Authentication: <Security />,
  Security: <Security />,
  Schedule: <Schedule />,
  FormatListNumbered: <FormatListNumbered />,
  Demo: <PlayCircleOutline />,
  PlayCircleOutline: <PlayCircleOutline />,
  Videocam: <Videocam />,
  VideoCall: <VideoCall />,
  Call: <Call />,
  Notifications: <Notifications />,
  Api: <Api />,
  IntegrationInstructions: <IntegrationInstructions />,
  RocketLaunch: <RocketLaunch />,
  Build: <Build />,
  Groups: <Groups />,
  People: <People />,
  VpnKey: <VpnKey />,
  Key: <Key />,
  Lock: <Lock />,
  LockOpen: <LockOpen />,
  Info: <Info />,
  HelpOutline: <HelpOutline />,
  Article: <Article />,
  MenuBook: <MenuBook />,
  Terminal: <Terminal />,
  Extension: <Extension />,
  Widgets: <Widgets />,
  Cloud: <Cloud />,
  Download: <Download />,
  GetApp: <GetApp />,
  BugReport: <BugReport />,
  Lightbulb: <Lightbulb />,
  TipsAndUpdates: <TipsAndUpdates />,
  Link: <Link />,
  Language: <Language />,
  Devices: <Devices />,
  Smartphone: <Smartphone />,
  Computer: <Computer />,
  Storage: <Storage />,
  Sync: <Sync />,
  School: <School />,
  LibraryBooks: <LibraryBooks />,
  Home: <Home />,
  Star: <Star />,
  CheckCircle: <CheckCircle />,
  Warning: <Warning />,
  Forum: <Forum />,
  Message: <Message />,
  Mic: <Mic />,
  ScreenShare: <ScreenShare />,
  Event: <Event />,
  CalendarMonth: <CalendarMonth />,
  AccessTime: <AccessTime />,
  Tune: <Tune />,
  Analytics: <Analytics />,
  Hub: <Hub />,
  AccountTree: <AccountTree />,
  DataObject: <DataObject />,
  Campaign: <Campaign />,
  Visibility: <Visibility />,
  Search: <Search />,
  FilterList: <FilterList />,
  Dashboard: <Dashboard />,
  Layers: <Layers />,
  Category: <Category />,
  Construction: <Construction />,
  Handyman: <Handyman />,
  Speed: <Speed />,
  Memory: <Memory />,
  Science: <Science />,
  Psychology: <Psychology />
};

const sdkIconMap: { [key: string]: React.ReactElement } = {
  android: <PhoneAndroid />,
  javascript: <Web />,
  'react-native': <PhoneIphone />,
  react: <Web />,
  ios: <PhoneIphone />,
  flutter: <PhoneIphone />,
  unity: <PhoneIphone />,
  dotnet: <Web />
};

const DocumentationDrawer: React.FC<DocumentationDrawerProps> = ({
  open,
  onClose,
  sections,
  selectedSectionId,
  onSectionSelect,
  sdkOptions,
  selectedSdk,
  onSdkChange,
  showSdkSelector = false,
  onGettingStartedClick,
  kitType = 'uikit'
}) => {
  const drawerWidth = 280;
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});

  const handleSdkChange = (event: SelectChangeEvent) => {
    onSdkChange(event.target.value);
  };

  const handleSectionToggle = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        display: { xs: open ? 'block' : 'none', md: 'block' },
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          top: '64px',
          height: 'calc(100vh - 64px)',
          borderRight: '1px solid #e0e0e0',
          overflowY: 'auto',
          position: { xs: 'fixed', md: 'fixed' },
          zIndex: { xs: 1100, md: 'auto' },
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: '#a8a8a8',
            },
          },
        },
      }}
    >
      {/* Language/SDK Selection Dropdown - Only show for kit sections */}
      {showSdkSelector && (
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 600 }}>
            {kitType === 'sdk' ? 'Select SDK' : 'Select UI Kit'}
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={selectedSdk}
              onChange={handleSdkChange}
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                },
              }}
            >
              {sdkOptions.map((sdk) => (
                <MenuItem key={sdk.id} value={sdk.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {sdkIconMap[sdk.id]}
                    <Typography variant="body2">{sdk.name}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Getting Started Button - Only show for UI Kit sections */}
      {showSdkSelector && onGettingStartedClick && (
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Button
            fullWidth
            variant="contained"
            onClick={onGettingStartedClick}
            startIcon={<PlayArrow />}
            sx={{
              backgroundColor: '#1976d2',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            Getting Started
          </Button>
        </Box>
      )}

      <List sx={{ pt: 1 }}>
        {sections.map((section) => (
          <React.Fragment key={section.id}>
            <ListItem disablePadding>
              <ListItemButton
                selected={selectedSectionId === section.id}
                onClick={() => {
                  if (section.subSections && section.subSections.length > 0) {
                    handleSectionToggle(section.id);
                  } else {
                    onSectionSelect(section.id);
                  }
                }}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#e3f2fd',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                    },
                  },
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {iconMap[section.icon] || <Description />}
                </ListItemIcon>
                <ListItemText 
                  primary={section.title}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: selectedSectionId === section.id ? 600 : 400
                  }}
                />
                {section.subSections && section.subSections.length > 0 && (
                  expandedSections[section.id] ? <ExpandLess /> : <ExpandMore />
                )}
              </ListItemButton>
            </ListItem>
            
            {/* Render subsections */}
            {section.subSections && section.subSections.length > 0 && (
              <Collapse in={expandedSections[section.id]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {section.subSections.map((subSection) => (
                    <ListItem key={subSection.id} disablePadding>
                      <ListItemButton
                        selected={selectedSectionId === subSection.id}
                        onClick={() => onSectionSelect(subSection.id)}
                        sx={{
                          pl: 6,
                          mx: 1,
                          borderRadius: 1,
                          '&.Mui-selected': {
                            backgroundColor: '#e3f2fd',
                            '&:hover': {
                              backgroundColor: '#e3f2fd',
                            },
                          },
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                      >
                        <ListItemText 
                          primary={subSection.title}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: selectedSectionId === subSection.id ? 600 : 400
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </Drawer>
  );
};

export default DocumentationDrawer;
